"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { supabase } from "@/lib/supabase";

// Superadmin "Run agent tests now" (docs/agent-lifecycle.md). Queues one full
// run per agent the superadmin can manage, recorded against them. The database
// then works through the runs in the background, as them, with the browser
// closed or open; progress and cancel are on the Automations page.
// queue_agent_test_batch / preview_agent_test_batch enforce the superadmin
// role and the "can manage" rule in the database.

type Scope = "all" | "reviewed" | "team";

type Preview = {
  agent_count: number;
  test_count: number;
  batch_in_progress: boolean;
};

type AgentTestBatchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Team spaces to offer for the "one team" scope. */
  teams: { id: string; name: string }[];
};

const SCOPE_LABELS: Record<Scope, string> = {
  all: "All Provisional and Reviewed agents",
  reviewed: "Reviewed agents only",
  team: "One team",
};

export function AgentTestBatchDialog({ open, onOpenChange, teams }: AgentTestBatchDialogProps) {
  const router = useRouter();
  const [scope, setScope] = useState<Scope>("all");
  const [teamId, setTeamId] = useState<string>("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [queuing, setQueuing] = useState(false);

  const scopeReady = scope !== "team" || Boolean(teamId);

  useEffect(() => {
    if (!open || !scopeReady) return;
    let cancelled = false;
    queueMicrotask(async () => {
      if (cancelled) return;
      setPreview(null);
      setPreviewError(null);
      const { data, error } = await supabase.rpc("preview_agent_test_batch", {
        p_scope: scope,
        p_team_id: scope === "team" ? teamId : null,
      });
      if (cancelled) return;
      if (error) {
        console.error("[AgentTestBatchDialog] preview failed:", error);
        setPreviewError(error.message || "Couldn't check which agents would be tested.");
        return;
      }
      setPreview(data as Preview);
    });
    return () => {
      cancelled = true;
    };
  }, [open, scope, teamId, scopeReady]);

  const blocked = !preview || preview.batch_in_progress || preview.agent_count === 0;

  const handleQueue = async () => {
    if (blocked) return;
    setQueuing(true);
    const { data, error } = await supabase.rpc("queue_agent_test_batch", {
      p_scope: scope,
      p_team_id: scope === "team" ? teamId : null,
    });
    setQueuing(false);
    if (error) {
      console.error("[AgentTestBatchDialog] queue failed:", error);
      toast.error(error.message || "Failed to start the batch.");
      return;
    }
    const result = data as { agent_count: number; test_count: number };
    toast.success(
      `Started ${result.agent_count} agent${result.agent_count === 1 ? "" : "s"} (${result.test_count} tests). They run in the background.`,
      { action: { label: "View runs", onClick: () => router.push("/automations?tab=runs") } },
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Run agent tests now</DialogTitle>
          <DialogDescription>
            Runs every test using each agent&apos;s own setup — not your access — and saves the scores under your
            name. It runs in the background, so you can close this page; follow it on the Automations page.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground" htmlFor="batch-scope">
              Agents
            </label>
            <Select value={scope} onValueChange={(value) => setScope(value as Scope)}>
              <SelectTrigger id="batch-scope" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(SCOPE_LABELS) as Scope[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {SCOPE_LABELS[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scope === "team" ? (
              <Select value={teamId} onValueChange={setTeamId}>
                <SelectTrigger className="w-full" aria-label="Team">
                  <SelectValue placeholder="Pick a team…" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>

          <div className="space-y-2 rounded-lg border border-border px-4 py-3 text-sm">
            {!scopeReady ? (
              <p className="text-muted-foreground">Pick a team to see what would run.</p>
            ) : previewError ? (
              <p className="text-destructive">{previewError}</p>
            ) : !preview ? (
              <p className="flex items-center gap-2 text-muted-foreground">
                <Spinner className="size-3.5" /> Checking…
              </p>
            ) : (
              <>
                <p className="tabular-nums text-foreground">
                  {preview.agent_count} agent{preview.agent_count === 1 ? "" : "s"} · {preview.test_count} test
                  {preview.test_count === 1 ? "" : "s"} · {preview.test_count * 2} model calls
                </p>
                <p className="text-xs text-muted-foreground">
                  Each test is answered by the agent, then graded. Agents with a full run already waiting are skipped.
                </p>
                {preview.batch_in_progress ? (
                  <p className="text-destructive">Your last batch still has runs waiting. Wait for it to finish, or cancel it on the Automations page.</p>
                ) : null}
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            Results count towards each agent&apos;s stage: a Reviewed agent that scores below its cut-off goes back to
            Provisional, and its owner and team admin are notified.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" disabled={queuing} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={blocked || queuing} onClick={() => void handleQueue()}>
            {queuing ? <Spinner className="size-3.5" /> : null}
            {queuing ? "Starting…" : "Run tests"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
